// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { cartlineContainerStyles } from "@synd/App/Components/Cart/CartLineContainer";
import { color, getSystemColor, xScreen } from "@synd/XDL/Utilities";
import { css } from "styled-components";

cartlineContainerStyles.container = {
    css: css`
        display: grid;
        grid-template-columns:
            96px minmax(120px, 1fr) minmax(0, 100px) minmax(0, 172px) minmax(0, 123px) minmax(0, 129px)
            ${({ includeActions }: any) => (includeActions ? css`minmax(0, 120px)` : css`minmax(0, 24px)`)};
        grid-column-gap: 12px;
        background: ${color("common.background")};
        border-bottom: 1px solid ${color("common.border")};
        padding: 12px 8px;
        width: 100%;

        ${xScreen("lte", "md")} {
            padding: 12px 0;
        }

        @media print {
            width: 100%;
            content: "";
            display: table;
            clear: both;
            -webkit-column-break-inside: avoid;
            page-break-inside: avoid;
            break-inside: avoid;
            border-bottom: 1px solid ${getSystemColor("common.border")};
        }
    `,
};

cartlineContainerStyles.error = {
    css: css`
        grid-column: 1 / 3;
        grid-row: 1 / 2;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 8;
        }
    `,
};

cartlineContainerStyles.image = {
    css: css`
        grid-column: 1 / 2;
        grid-row: 2 / 5;
        ${xScreen("lte", "md")} {
            grid-row: 2 / 4;
        }
        @media print {
            min-width: 120px;
            min-height: 120px;
            margin-bottom: 4px;
            float: left;
        }
    `,
};
cartlineContainerStyles.description = {
    css: css`
        ${({ isCondensed }: any) => (!isCondensed ? "grid-column: 2 / 3;" : " grid-column: 1 / 3;")};
        grid-row: 2 / 3;
        position: relative;
        padding-right: 12px;
        ${xScreen("lte", "md")} {
            ${({ isCondensed }: any) => (!isCondensed ? "grid-column: 2 / 7; " : " grid-column: 1 / 7;")};
            grid-row: 2 / 4;
            padding-right: 0;
        }
        @media print {
            min-width: 75%;
            max-width: 75%;
            min-height: 120px;
            float: left;
        }
    `,
};
cartlineContainerStyles.infoSmallScreen = {
    css: css`
        grid-column: 1 / 7;
        grid-row: 4 / 5;
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        padding-top: 12px;
        > div {
            width: calc(50% - 6px);
        }
        @media print {
            display: block;
            float: left;
            width: 100%;
        }
    `,
};
cartlineContainerStyles.cases = {
    css: css`
        grid-column: 3 / 4;
        grid-row: 2 / 3;
        position: relative;
        padding-right: 12px;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 4;
            grid-row: 4 / 5;
            padding: 0;
        }
        @media print {
            min-width: 25%;
            max-width: 25%;
            min-height: 50px;
            float: left;
            padding: 0;
            clear: left;
        }
    `,
};
cartlineContainerStyles.price = {
    css: css`
        grid-column: 4 / 5;
        grid-row: 2 / 3;
        position: relative;
        padding-right: 12px;
        ${xScreen("lte", "md")} {
            grid-column: 4 / 7;
            grid-row: 4 / 5;
            padding: 0;
        }
        @media print {
            min-width: 25%;
            max-width: 25%;
            min-height: 50px;
            float: left;
            padding: 0;
        }
    `,
};
cartlineContainerStyles.qty = {
    css: css`
        grid-column: 5 / 6;
        grid-row: 2 / 3;
        padding-right: 12px;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 4;
            grid-row: 5 / 6;
            padding-right: 0;
        }
        @media print {
            margin-right: 0;
            min-width: 25%;
            max-width: 25%;
            min-height: 50px;
            float: left;
        }
    `,
};
cartlineContainerStyles.total = {
    css: css`
        grid-column: 6 / 7;
        grid-row: 2 / 3;
        position: relative;
        padding-right: 12px;
        ${xScreen("lte", "md")} {
            grid-column: 4 / 7;
            grid-row: 5 / 6;
            padding-right: 0;
        }
        @media print {
            min-width: 25%;
            max-width: 25%;
            min-height: 50px;
            float: right;
        }
    `,
};
cartlineContainerStyles.remove = {
    css: css`
        grid-column: 7 / 8;
        grid-row: 2 / 3;
        padding-top: 2px;
        margin-left: auto;
        ${xScreen("lte", "md")} {
            padding-top: 3px;
        }
    `,
};
cartlineContainerStyles.actions = {
    css: css`
        ${({ isCondensed }: any) => (!isCondensed ? "grid-column: 2 / 3;" : " grid-column: 1 / 3;")};
        grid-row: 3 / 4;
        position: relative;
        padding-right: 12px;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 8;
            grid-row: 6 / 7;
            padding-right: 0;
        }
        @media print {
            display: none;
        }
    `,
};

cartlineContainerStyles.notes = {
    css: css`
        grid-column: 2 / 3;
        position: relative;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 8;
            grid-row: 6 / 7;
        }

        &:empty {
            display: none;
        }
    `,
};
